"use client";

import {
  toast as sonnerToast,
  Toaster as SonnerToaster,
  type ToasterProps as SonnerToasterProps,
  type ToastT,
} from "sonner";

import { cn } from "../../utils";
import { ToasterContent } from "./components";
import {
  type Toast,
  type ToastFunction,
  type ToastProps,
  type ToastVariantFunction,
  toastVariants,
} from "./Toaster.types";

export const Toaster: React.FC<SonnerToasterProps> = ({
  className,
  gap = 32,
  expand = true,
  duration = 10000,
  visibleToasts = 3,
  ...rest
}) => {
  return (
    <SonnerToaster
      gap={gap}
      expand={expand}
      duration={duration}
      position="top-right"
      visibleToasts={visibleToasts}
      toastOptions={{
        className: cn("ui:pointer-events-auto", className),
      }}
      {...rest}
    />
  );
};

const getOptions = (props: ToastProps) => ({
  ...(props?.id ? { id: props.id } : {}),
  duration: props.duration,
  onDismiss: ({ id }: ToastT) => props.onDismiss?.(id),
  onAutoClose: ({ id }: ToastT) => props.onAutoClose?.(id),
});

const baseMethod: ToastFunction = (props) => {
  const variant = props.variant ?? "info";
  return sonnerToast.custom(
    (id) => <ToasterContent id={id} variant={variant} {...props} />,
    getOptions(props),
  );
};

const variantMethods = Object.fromEntries(
  toastVariants.map((variant) => [
    variant,
    (props: Omit<ToastProps, "variant">) =>
      sonnerToast.custom(
        (id) => <ToasterContent id={id} variant={variant} {...props} />,
        getOptions(props),
      ),
  ]),
) as Record<(typeof toastVariants)[number], ToastVariantFunction>;

/**
 * Toast notification system that provides both direct and variant-specific methods.
 *
 * @example
 * ```ts
 * // Direct usage with variant
 * toast({
 *   title: "Success!",
 *   variant: "success",
 *   description: "Operation completed"
 * });
 *
 * // Using variant-specific methods
 * toast.success({
 *   title: "Success!",
 *   description: "Operation completed"
 * });
 *
 * // With action button
 * toast({
 *   title: "New Update",
 *   description: "Check out our latest features",
 *   action: {
 *     label: "Learn More",
 *     onClick: () => window.open("https://example.com")
 *   }
 * });
 * ```
 */
export const toast: Toast = Object.assign(baseMethod, variantMethods);
